import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';

export type DatePreset =
  | 'today' | 'yesterday'
  | 'last_7d' | 'last_14d' | 'last_30d' | 'last_90d'
  | 'this_month' | 'last_month'
  | 'lifetime'
  | 'custom';

export interface DateRangeValue {
  preset: DatePreset;
  /** ISO yyyy-mm-dd; only set when preset === 'custom' */
  since?: string;
  until?: string;
}

interface Props {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  className?: string;
}

const PRESET_LABELS: Record<DatePreset, string> = {
  today:        'Today',
  yesterday:    'Yesterday',
  last_7d:      'Last 7 days',
  last_14d:     'Last 14 days',
  last_30d:     'Last 30 days',
  last_90d:     'Last 90 days',
  this_month:   'This month',
  last_month:   'Last month',
  lifetime:     'Lifetime',
  custom:       'Custom range',
};

const PRESET_ORDER: DatePreset[] = [
  'today', 'yesterday', 'last_7d', 'last_14d', 'last_30d', 'last_90d',
  'this_month', 'last_month', 'lifetime', 'custom',
];

export function describeRange(v: DateRangeValue): string {
  if (v.preset === 'custom' && v.since && v.until) return `${v.since} → ${v.until}`;
  return PRESET_LABELS[v.preset];
}

/**
 * Date-range picker dropdown — preset list + custom from/to inputs.
 * Designed for analytics widgets (Meta / TikTok / Google Ads).
 */
export const DateRangePicker: React.FC<Props> = ({ value, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const [draftSince, setDraftSince] = useState(value.since ?? '');
  const [draftUntil, setDraftUntil] = useState(value.until ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const pick = (preset: DatePreset) => {
    if (preset === 'custom') {
      // Don't close — let the user enter dates
      onChange({ preset: 'custom', since: draftSince, until: draftUntil });
      return;
    }
    onChange({ preset });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draftSince || !draftUntil) return;
    onChange({ preset: 'custom', since: draftSince, until: draftUntil });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={clsx('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-xs font-medium text-text-primary hover:border-border-strong"
      >
        <Calendar size={13} className="text-text-muted" />
        <span>{describeRange(value)}</span>
        <ChevronDown size={12} className={clsx('text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 flex w-[400px] overflow-hidden rounded-xl border border-border bg-surface shadow-elevated animate-fade-in">
          {/* Preset list */}
          <ul className="w-44 border-r border-border py-1">
            {PRESET_ORDER.map((p) => {
              const active = value.preset === p;
              return (
                <li key={p}>
                  <button
                    type="button"
                    onClick={() => pick(p)}
                    className={clsx(
                      'flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors',
                      active ? 'bg-primary-soft text-primary' : 'text-text-primary hover:bg-surface-alt',
                    )}
                  >
                    {PRESET_LABELS[p]}
                    {active && <Check size={12} className="text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Custom range form */}
          <div className="flex-1 p-3">
            <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-text-muted">Custom range</p>
            <div className="space-y-2">
              <div>
                <label className="mb-0.5 block text-2xs text-text-muted">From</label>
                <Input
                  type="date"
                  value={draftSince}
                  onChange={(e) => setDraftSince(e.target.value)}
                  className="!h-8 !text-xs"
                />
              </div>
              <div>
                <label className="mb-0.5 block text-2xs text-text-muted">To</label>
                <Input
                  type="date"
                  value={draftUntil}
                  onChange={(e) => setDraftUntil(e.target.value)}
                  className="!h-8 !text-xs"
                />
              </div>
            </div>
            <Button
              size="sm"
              onClick={applyCustom}
              disabled={!draftSince || !draftUntil}
              className="mt-3 w-full"
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Convert a DateRangeValue into the query params the backend expects. */
export function dateRangeToParams(v: DateRangeValue): { datePreset?: string; since?: string; until?: string } {
  if (v.preset === 'custom' && v.since && v.until) {
    return { since: v.since, until: v.until };
  }
  if (v.preset === 'custom') return {};
  return { datePreset: v.preset };
}
